<template>
  <el-container class="background-white">
    <el-header class="header-container">
      <h1 class="header-title">Uncaptured Orders (Shopify)</h1>
    </el-header>

    <el-main class="main-content">
      <!-- Top Controls Row -->
      <el-row :gutter="20" class="controls-row" align="middle">
        <el-col :xs="24" :sm="8">
          <el-switch
              v-model="autoRefresh"
              :disabled="loading"
              active-text="Auto-Refresh On"
              inactive-text="Auto-Refresh Off"
              class="custom-switch">
          </el-switch>
        </el-col>

        <!-- Combined Filters in One Row -->
        <el-col :xs="24" :sm="8" class="filter-container">
          <el-select
              v-model="salesChannel"
              size="mini"
              placeholder="Select Status"
              @change="fetch"
              class="dropdown custom-select"
          >
            <el-option
                v-for="item in salesChannelOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value">
            </el-option>
          </el-select>
        </el-col>

        <el-col :xs="24" :sm="8" class="text-right">
          <el-button
              type="primary"
              size="mini"
              @click="fetch"
              :disabled="loading || autoRefresh"
              style="margin-right: 10px">
            <i class="el-icon-refresh"></i>
            Refresh
          </el-button>
        </el-col>
      </el-row>

      <!-- Orders Table -->
      <el-row>
        <el-col :span="24">
          <div class="table-container">
            <el-table
                v-loading="loading"
                :data="orders"
                class="order-table"
                stripe
                border
                max-height="600">
              <!-- Table Columns -->
              <el-table-column
                  prop="sales_channel"
                  label="Sales Channel"
                  min-width="150">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.sales_channel }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="sales_channel_reference"
                  label="Order ID"
                  min-width="160">
                <template #default="{ row }">
                  <div class="order-id-cell">
                    <a
                        v-if="row.order_link"
                        :href="row.order_link"
                        class="order-id"
                        target="_blank"
                        rel="noopener noreferrer">
                      {{ row.sales_channel_reference }}
                    </a>
                    <span v-else class="order-id">{{ row.sales_channel_reference }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                  prop="status"
                  label="Order Status"
                  min-width="180">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.status }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="financial_status"
                  label="Financial Status"
                  min-width="180">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.financial_status }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="total_price"
                  label="Total">
                <template #default="{ row }">
                  <span class="table-cell">{{ formatCurrency(row.total_price) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="total_authorized"
                  label="Auth">
                <template #default="{ row }">
                  <span class="table-cell">{{ formatCurrency(row.total_authorized) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="total_adjustment"
                  label="Adjusted">
                <template #default="{ row }">
                  <span class="table-cell">{{ formatCurrency(row.total_adjustment) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="total_captured"
                  label="Captured">
                <template #default="{ row }">
                  <span class="table-cell">{{ formatCurrency(row.total_captured) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  label="Created at"
                  min-width="180">
                <template #default="{ row }">
                  <span class="date-cell">{{ displayDate(row.created_at) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>
    </el-main>
  </el-container>
</template>

<style scoped>
.controls-row {
  margin-bottom: 25px;
  align-items: center;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.filter-container {
  display: flex;
  gap: 10px; /* Adds space between filters */
  align-items: center;
}

.dropdown.custom-select {
  width: 100%;
}

.additional-filter {
  width: 100%;
}

.text-right {
  text-align: right;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 25px;
  border-bottom: 2px solid #e8e8e8;
  background: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.main-content {
  padding: 25px;
  background: #fff;
}

.controls-row {
  margin-bottom: 25px;
  align-items: center;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.custom-switch {
  font-weight: 500;
}

.dropdown.custom-select {
  margin-right: 10px;
  width: 100%;
  border-radius: 6px;
}

.refresh-button {
  transition: all 0.3s ease;
  font-weight: 500;
}

.refresh-button:hover {
  transform: scale(1.02);
}

.table-container {
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.order-table {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.order-id-cell {
  display: flex;
  flex-direction: column;
}

.table-cell {
  color: #2c3e50;
  font-weight: 500;
}

.order-id {
  color: #409EFF;
  font-weight: 600;
}

.date-cell {
  color: #606266;
  font-size: 0.9em;
}

.action-button {
  transition: all 0.2s ease;
  border-radius: 6px;
}

.action-button:hover {
  transform: scale(1.05);
}

@media screen and (max-width: 768px) {
  .controls-row .el-col {
    margin-bottom: 15px;
    text-align: left !important;
  }

  .header-title {
    font-size: 20px;
  }

  .main-content {
    padding: 15px;
  }
}
</style>

<script>
import axios from '../../../repositories';

export default {
  name: 'UncapturedOrdersPage',
  created() {
    this.fetch();
  },
  data() {
    return {
      autoRefresh: false,
      autoRefreshInterval: 10000,
      refreshInterval: null,
      loading: true,
      orders: [],
      salesChannel: 'all',
      salesChannelOptions: [
        {value: 'all', label: 'All Sales Channels'},
        {value: 'shopify_ca', label: 'Shopify CA'},
        {value: 'shopify_usa', label: 'Shopify US'},
      ],
    };
  },
  methods: {
    async fetch() {
      this.loading = true;

      try {
        // Fetch data from the API with both filters applied
        const response = await axios.get('/oms/orders/uncaptured-orders', {
          params: {
            salesChannel: this.salesChannel,
          },
        });

        if (response.data?.success === 'true' || response.data?.success === true) {
          this.orders = response.data.orders;
        } else {
          this.$message.error(response.data?.message || 'Error loading orders');
        }
      } catch (err) {
        this.$message.error(err.message || 'Repository error occurred');
      } finally {
        this.loading = false;
      }
    },
    displayDate(date) {
      if (!date) {
        return '-';
      }

      try {
        const formattedDate = new Date(date);
        return formattedDate.toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Use 24-hour format
          timeZoneName: 'short', // Show time zone
        });
      } catch (error) {
        console.error('Invalid date format:', date);
        return '-';
      }
    },
    formatCurrency(value) {
      const number = parseFloat(value);
      if (isNaN(number)) return '0.00';
      return number.toFixed(2);
    },
  },
  watch: {
    autoRefresh: function (val) {
      if (val) {
        return this.refreshInterval = setInterval(
            () => this.fetch(),
            this.autoRefreshInterval,
        );
      }

      clearTimeout(this.refreshInterval);
    },
  },
};
</script>
